import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Close } from '@mui/icons-material';
import { Box, Button, Divider, Drawer, IconButton, List, Stack, Typography } from '@mui/material';

import { UserListItems, ConstantListItems, DataListItems, MainListItems } from './admin-list-items';

import { HelpIconLink } from '@features/help-icon-link';
import { useAuth } from '@providers/context/AuthContext';
import { DEFAULT_PAGE_TITLE } from '@shared/api/constants';

const drawerWidth = 240;

const getPageTitle = (pathname) => {
    const routesMap = {
        '/admin': 'dashboard',
        '/admin/registration': 'registration',
        '/admin/users': 'users',
        '/admin/bags': 'bags',
        '/admin/batches': 'batches',
        '/admin/orders': 'orders',
        '/admin/packs': 'packs',
        '/admin/countries': 'countries',
        '/admin/processes': 'processes',
        '/admin/roast-types': 'roast types',
        '/admin/types': 'types',
        '/admin/varieties': 'varieties',
        '/admin/tasks': 'tasks',
        '/admin/documentation': 'documentation',
        '/admin/create-sticker': 'label creator',
        '/admin/buckets': 'buckets',
    };

    const getDynamicRouteTitle = (path) => {
        const dynamicRoutes = [
            { pattern: /^\/admin\/batches\/[^/]+$/, title: 'batch details' },
            { pattern: /^\/admin\/bags\/[^/]+$/, title: 'bag details' },
            { pattern: /^\/admin\/packs\/[^/]+$/, title: 'pack details' },
            { pattern: /^\/admin\/orders\/[^/]+$/, title: 'order details' },
        ];

        for (const route of dynamicRoutes) {
            if (route.pattern.test(path)) {
                return route.title;
            }
        }

        return null;
    };

    if (routesMap[pathname]) {
        return routesMap[pathname];
    }

    const dynamicTitle = getDynamicRouteTitle(pathname);
    if (dynamicTitle) {
        return dynamicTitle;
    }

    return 'unknown page';
};

const Header = ({ namePage }) => {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const onClickLogout = async () => {
        const data = await logout.mutateAsync();
        if (data) navigate('/login');
    };

    return (
        <Stack
            direction="row"
            sx={{
                backgroundColor: '#000000FF',
                display: { xs: 'none', md: 'flex' },
                width: '100%',
                height: '70px',
                minHeight: '70px',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                pr: 1.5,
            }}
            spacing={2}
        >
            <Box
                sx={{
                    width: drawerWidth,
                    display: 'flex',
                    height: '100%',
                    alignItems: 'center',
                    pl: 2.5,
                    cursor: 'pointer',
                }}
                onClick={() => navigate('/admin')}
            >
                <img
                    src="/logo.svg"
                    alt="Logo"
                    style={{
                        height: 50,
                        width: 'auto',
                    }}
                />
            </Box>
            <Typography
                component="h1"
                variant="h6"
                color="#ffffff"
                noWrap
                sx={{ flexGrow: 1, textAlign: 'center' }}
            >
                {namePage}
            </Typography>
            <Stack direction="row" sx={{ gap: 1 }}>
                <HelpIconLink path="/admin/documentation"/>
                <Button onClick={() => navigate('/')} size="medium" variant="outlined"
                        sx={{ filter: 'invert(1)', borderWidth: '2px', mr: 1.5 }}>
                    user page
                </Button>
                <Button onClick={onClickLogout} size="medium" variant="contained" color="error">
                    log out
                </Button>
            </Stack>
        </Stack>
    );
};

const SideMenu = () => {
    const { userInfo } = useAuth();

    return (
        <Drawer
            variant="permanent"
            PaperProps={{ className: 'no-scrollbar' }}
            sx={{
                width: drawerWidth,
                display: { xs: 'none', md: 'flex' },
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                    backgroundColor: '#F5F5F5',
                    display: { xs: 'none', md: 'flex' },
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    boxShadow: 12,
                    overflowX: 'hidden',
                },
            }}
        >
            <List component="nav" className="no-scrollbar" sx={{ pt: 0, flexGrow: 1, overflowY: 'auto' }}>
                {/*<Divider/>*/}
                <MainListItems/>
                <Divider textAlign="left"></Divider>
                <UserListItems/>
                <Divider textAlign="left"></Divider>
                <DataListItems/>
                <Divider textAlign="left"></Divider>
                <ConstantListItems/>
            </List>
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: '14px' }}>
                    {userInfo?.firstName || ''} {userInfo?.lastName || ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {userInfo?.accountType?.name || ''}
                </Typography>
            </Box>
        </Drawer>
    );
};

const HeaderMobile = ({ onMenuOpen, namePage }) => {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const onClickLogout = async () => {
        const data = await logout.mutateAsync();
        if (data) navigate('/login');
    };

    return (
        <Stack
            direction="row"
            sx={{
                backgroundColor: '#000000FF',
                display: { xs: 'flex', md: 'none' },
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
            }}
        >
            <IconButton onClick={onMenuOpen}>
                <Menu sx={{ color: '#fafafa' }}/>
            </IconButton>
            <Typography
                component="h1"
                variant="h6"
                color="#fafafa"
                noWrap
                sx={{ flexGrow: 1 }}
            >
                {namePage}
            </Typography>
            <Button onClick={() => navigate('/')} size="small" variant="outlined"
                    sx={{ filter: 'invert(1)', borderWidth: '2px', mr: 1.5 }}>
                user page
            </Button>
            <Button onClick={onClickLogout} size="small" variant="contained" color="error">
                log out
            </Button>
        </Stack>
    );
};

const SideMenuMobile = ({ open, onClose }) => {
    const { userInfo } = useAuth();
    const navigate = useNavigate();

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            PaperProps={{ className: 'no-scrollbar' }}
            sx={{
                '& .MuiDrawer-paper': {
                    height: '100%', // Убедитесь, что высота задана
                    display: 'flex', // Для правильного расположения flex-элементов
                    flexDirection: 'column',
                    justifyContent: 'space-between', // Равномерное распределениеы
                    width: drawerWidth,
                    boxSizing: 'border-box',
                    backgroundColor: '#F5F5F5',
                },
            }}
        >
            <Box sx={{
                p: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: 1,
                        cursor: 'pointer',
                    }}
                    onClick={() => navigate('/admin')}
                >
                    <img
                        src="/logo.svg"
                        alt="Logo"
                        style={{
                            height: 40,
                            filter: 'invert(1)',
                            width: 'auto',
                        }}
                    />
                    {/*<img*/}
                    {/*    src="/coffee-pack.svg"*/}
                    {/*    alt="Logo"*/}
                    {/*    style={{*/}
                    {/*        height: 40,*/}
                    {/*        width: 'auto',*/}
                    {/*    }}*/}
                    {/*/>*/}
                </Box>
                <IconButton onClick={onClose}>
                    <Close/>
                </IconButton>
            </Box>
            <List component="nav" className="no-scrollbar" sx={{
                pt: 0, alignSelf: 'flex-start', // Подтягиваем список к верхнему краю
                flexGrow: 0,
            }}>
                <Divider/>
                <MainListItems onClose={onClose}/>
                <Divider textAlign="left"></Divider>
                <UserListItems onClose={onClose}/>
                <Divider textAlign="left"></Divider>
                <DataListItems onClose={onClose}/>
                <Divider textAlign="left"></Divider>
                <ConstantListItems onClose={onClose}/>
            </List>
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    mt: 'auto',
                    backgroundColor: '#F5F5F5',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: '14px' }}>
                    {userInfo?.firstName || ''} {userInfo?.lastName || ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {userInfo?.accountType?.name || ''}
                </Typography>
            </Box>
        </Drawer>
    );
};

export const AdminHeader = () => {
    const location = useLocation();

    const [isMenuOpen, setMenuOpen] = useState(false);

    const handleMenuOpen = () => setMenuOpen(true);
    const handleMenuClose = () => setMenuOpen(false);

    const namePage = getPageTitle(location.pathname);

    useEffect(() => {
        document.title = `${namePage} ⎯ ` + DEFAULT_PAGE_TITLE;
    }, [namePage]);

    return (
        <Box
            className="no-scrollbar"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                width: '100%',
            }}
        >
            {/* Header Components */}
            <HeaderMobile onMenuOpen={handleMenuOpen} namePage={namePage}/>
            <Header namePage={namePage}/>
            {/* Side Menu */}
            <SideMenuMobile open={isMenuOpen} onClose={handleMenuClose}/>
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                <SideMenu/>
                <Box
                    component="main"
                    className="no-scrollbar"
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light' ? '#fafafa' : theme.palette.grey[900],
                        p: { xs: 1, md: 2 }
                    }}
                >
                    <Outlet/>
                </Box>
            </Box>
        </Box>
    );
};
